import {
  Alert,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Spin,
  Switch,
} from 'antd';
import styles from './academyCodes.module.scss';
import {fieldRules} from 'utils/helper';
import {walletCurrencies} from 'utils/mock';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {useMutation, useQuery} from '@apollo/client';
import {
  CREATE_ACADEMY_CODE,
  GET_SINGLE_ACADEMY_CODE,
  UPDATE_ACADEMY_CODE,
  couponOwnerTypes,
} from './resolvers';
import {CouponOwnerPicker} from './CouponOwnerPicker';
import {toast} from 'react-toastify';
import {useEffect, useRef, useState} from 'react';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import moment from 'moment';

export const CreateAcademyCode = ({close, id, form, refetch}) => {
  const [openModal, setOpenModal] = useState(false);
  const discountType = Form.useWatch('type', form);
  const promoCodeType = Form.useWatch('promoCodeType', form);
  const ownerType = Form.useWatch('ownerType', form);
  const isOwned = ownerType === 'AGENT' || ownerType === 'REFERRAL_PARTNER';
  // Ownership is only sent on update when it actually changed — sending one
  // half of the pair is interpreted against the stored value server-side.
  const initialOwnership = useRef({ownerType: undefined, ownerId: undefined});

  const {data, loading} = useQuery(GET_SINGLE_ACADEMY_CODE, {
    variables: {
      getCouponByIdId: id,
    },
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
    onError: error => toast.error(error.message),
    skip: !id,
  });

  const [updateCoupon, {loading: loadingUpdate}] = useMutation(
    UPDATE_ACADEMY_CODE,
    {
      onError: error => toast.error(error.message),
      errorPolicy: 'all',
    },
  );

  useEffect(() => {
    if (id && data?.getCouponById?.data) {
      const {startDate, endDate, fixedDiscountCoupon, ...rest} =
        data?.getCouponById?.data;
      // `owner` is the populated document, not a form value.
      delete rest.owner;
      initialOwnership.current = {
        ownerType: rest.ownerType || 'PLATFORM',
        ownerId: rest.ownerId || undefined,
      };
      form.setFieldsValue({
        ...rest,
        ownerType: rest.ownerType || 'PLATFORM',
        ownerId: rest.ownerId || undefined,
        ...(fixedDiscountCoupon && {
          amount: fixedDiscountCoupon.amount,
          currency: fixedDiscountCoupon.currency,
        }),
        startDate: startDate ? moment(startDate) : null,
        endDate: endDate ? moment(endDate) : null,
      });
    }
  }, [data]);

  const [createCoupon, {loading: loadingCreate}] = useMutation(
    CREATE_ACADEMY_CODE,
    {
      onError: error => toast.error(error.message),
      errorPolicy: 'all',
    },
  );

  const ownershipInput = isUpdate => {
    const nextType = form.getFieldValue('ownerType') || 'PLATFORM';
    const nextId =
      nextType === 'PLATFORM' ? null : form.getFieldValue('ownerId') || null;

    if (isUpdate) {
      const previous = initialOwnership.current;
      const unchanged =
        (previous.ownerType || 'PLATFORM') === nextType &&
        (previous.ownerId || null) === nextId;
      // Omit both fields entirely to leave stored ownership untouched.
      if (unchanged) return {};
    }

    return {ownerType: nextType, ownerId: nextId};
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spin />
        <h2>Loading...</h2>
      </div>
    );
  }

  return (
    <div className={styles.createRoot}>
      <h2>{id ? 'Edit' : 'Create'} Promo Code</h2>
      <Form onFinish={() => setOpenModal(true)} form={form} layout="vertical">
        <Form.Item
          rules={[fieldRules('This')]}
          name="coupon"
          className={styles.field}
          label="Promo Code Name">
          <Input />
        </Form.Item>
        <Form.Item
          rules={[fieldRules('This')]}
          name="type"
          className={styles.field}
          label="Discount Type">
          <Select>
            <Select.Option value="VARIABLE">Percentage</Select.Option>
            <Select.Option value="FIXED">Fixed Amount</Select.Option>
          </Select>
        </Form.Item>
        {discountType === 'VARIABLE' && (
          <Form.Item
            rules={[fieldRules('This')]}
            className={styles.field}
            name="value"
            label="Discount Percentage(%)">
            <InputNumber
              className="input-number"
              min={0}
              max={100}
              placeholder="10%"
              type="number"
            />
          </Form.Item>
        )}
        {discountType === 'FIXED' && (
          <div className={styles.fixedDiscount}>
            <Form.Item
              className={styles.field}
              name="currency"
              label="Currency">
              <Select allowClear>
                {walletCurrencies.map(currency => (
                  <Select.Option key={currency.key}>
                    {currency?.key}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              className={styles.field}
              rules={[fieldRules('This')]}
              name="amount"
              label="Discount Amount">
              <InputNumber className="input-number" min={0} type="number" />
            </Form.Item>
          </div>
        )}
        <Form.Item
          name="ownerType"
          initialValue="PLATFORM"
          className={styles.field}
          label="Code Owner Type"
          extra="Platform codes are plain discounts. Owned codes attribute Academy sales to a sales agent, or pay a referral partner commission.">
          <Select
            onChange={value => {
              form.setFieldsValue({ownerId: undefined});
              // Redemption for owned codes is counted on the Academy side, so
              // a LIMITED counter never moves here and eventually blocks the
              // code. Default owned codes to UNLIMITED.
              if (value === 'AGENT' || value === 'REFERRAL_PARTNER') {
                form.setFieldsValue({promoCodeType: 'UNLIMITED'});
              }
            }}>
            {couponOwnerTypes.map(type => (
              <Select.Option key={type.value} value={type.value}>
                {type.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        {isOwned && (
          <Form.Item
            name="ownerId"
            className={styles.field}
            label="Owner"
            rules={[
              {
                required: true,
                message:
                  'Owner is required for AGENT or REFERRAL_PARTNER coupons',
              },
            ]}>
            <CouponOwnerPicker ownerType={ownerType} />
          </Form.Item>
        )}
        <Form.Item
          rules={[fieldRules('This')]}
          name="promoCodeType"
          className={styles.field}
          label="Promo Code Type">
          <Select>
            <Select.Option value="LIMITED">Limited</Select.Option>
            <Select.Option value="UNLIMITED">Unlimited</Select.Option>
          </Select>
        </Form.Item>
        {isOwned && promoCodeType === 'LIMITED' && (
          <Alert
            type="warning"
            showIcon
            style={{marginBottom: 16}}
            message="Owned codes should be Unlimited. Redemptions are counted on the Academy side, so this counter never moves — once the limit reads zero the code stops validating."
          />
        )}
        {promoCodeType === 'LIMITED' && (
          <Form.Item
            className={styles.field}
            rules={[fieldRules('This')]}
            name="couponUsageLimit"
            label="Promo Code Limit">
            <InputNumber
              className="input-number"
              min={0}
              placeholder="10"
              type="number"
            />
          </Form.Item>
        )}
        <div className={styles.fixedDiscount}>
          <Form.Item
            className={styles.field}
            rules={[fieldRules('This')]}
            label="Start Date"
            name="startDate">
            <DatePicker placeholder="Start Date" showTime allowClear />
          </Form.Item>
          <Form.Item
            className={styles.field}
            rules={[fieldRules('This')]}
            label="End Date"
            name="endDate">
            <DatePicker placeholder="End Date" showTime allowClear />
          </Form.Item>
        </div>
        <Form.Item
          initialValue={id ? data?.getCouponById?.data?.isEnabled : false}
          name="isEnabled"
          label="Enable Promo Code">
          <Switch
            defaultChecked={id ? data?.getCouponById?.data?.isEnabled : false}
          />
        </Form.Item>
        <SubmitButton label="Proceed" />
        <ConfirmModal
          openModal={openModal}
          setOpenModal={() => setOpenModal(false)}
          loading={loadingCreate || loadingUpdate}
          title={`${id ? 'Update' : 'Create'} Promo Code`}
          confirmText="Please Make sure all fields are correctly filled"
          handleOperation={() => {
            if (id) {
              return updateCoupon({
                variables: {
                  input: {
                    id,
                    ...ownershipInput(true),
                    coupon: form.getFieldValue('coupon'),
                    type: form.getFieldValue('type'),
                    promoCodeType: form.getFieldValue('promoCodeType'),
                    ...(discountType === 'FIXED'
                      ? {
                          fixedDiscountCoupon: {
                            amount: form.getFieldValue('amount'),
                            currency: form.getFieldValue('currency'),
                          },
                        }
                      : {
                          value: form.getFieldValue('value'),
                        }),
                    ...(promoCodeType === 'LIMITED' && {
                      couponUsageLimit: form.getFieldValue('couponUsageLimit'),
                    }),
                    couponUsageLimit: form.getFieldValue('couponUsageLimit'),
                    startDate: moment(form.getFieldValue('startDate')).format(),
                    endDate: moment(form.getFieldValue('endDate')).format(),
                    isEnabled: form.getFieldValue('isEnabled') || false,
                  },
                },
              })
                .then(({data: {updateCoupon}}) => {
                  if (
                    updateCoupon.statusCode === 400 ||
                    updateCoupon.__typename === 'Error'
                  ) {
                    toast.error(updateCoupon.message);
                  } else {
                    toast.success('Promo Code Updated Successfully');
                    close();
                    setOpenModal(false);
                    refetch();
                  }
                })
                .catch(error => {
                  toast.error(error.message);
                });
            } else
              createCoupon({
                variables: {
                  input: {
                    ...ownershipInput(false),
                    coupon: form.getFieldValue('coupon'),
                    type: form.getFieldValue('type'),
                    promoCodeType: form.getFieldValue('promoCodeType'),
                    ...(discountType === 'FIXED'
                      ? {
                          fixedDiscountCoupon: {
                            amount: form.getFieldValue('amount'),
                            currency: form.getFieldValue('currency'),
                          },
                        }
                      : {
                          value: form.getFieldValue('value'),
                        }),
                    ...(promoCodeType === 'LIMITED' && {
                      couponUsageLimit: form.getFieldValue('couponUsageLimit'),
                    }),
                    couponUsageLimit: form.getFieldValue('couponUsageLimit'),
                    startDate: moment(form.getFieldValue('startDate')).format(),
                    endDate: moment(form.getFieldValue('endDate')).format(),
                    isEnabled: form.getFieldValue('isEnabled') || false,
                  },
                },
              })
                .then(({data: {createCoupon}}) => {
                  if (
                    createCoupon.statusCode === 400 ||
                    createCoupon.__typename === 'Error'
                  ) {
                    toast.error(createCoupon.message);
                  } else {
                    toast.success('Promo Code Created Successfully');
                    close();
                    setOpenModal(false);
                    refetch();
                    form.resetFields();
                  }
                })
                .catch(error => {
                  toast.error(error.message);
                });
          }}
        />
      </Form>
    </div>
  );
};
